import { useState, useMemo } from 'react'
import ResultsPanel from '@/components/ResultsPanel'

interface ACInputs {
  capacityTons: number
  currentSEER: number
  newSEER: number
  annualRunHours: number
  electricityRate: number
  installationCost: number
}

const DEFAULT: ACInputs = {
  capacityTons: 3,
  currentSEER: 13,
  newSEER: 18,
  annualRunHours: 1500,
  electricityRate: 0.15,
  installationCost: 5000,
}

interface SliderFieldProps {
  id: string
  label: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  prefix?: string
  decimals?: number
  onChange: (v: number) => void
  accentClass?: string
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  unit,
  prefix = '',
  decimals = 0,
  onChange,
}: SliderFieldProps) {
  const pct = ((value - min) / (max - min)) * 100
  const display = decimals > 0 ? value.toFixed(decimals) : value

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium text-slate-300">
          {label}
        </label>
        <span
          aria-live="polite"
          aria-label={`${label}: ${prefix}${display}${unit}`}
          className="text-sm font-bold tabular-nums px-2.5 py-0.5 rounded-md bg-blue-500/15 text-blue-300"
        >
          {prefix}{display}{unit}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-blue-600"
        style={{
          backgroundImage: `linear-gradient(to right, #2563eb ${pct}%, transparent ${pct}%)`,
        }}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${prefix}${display}${unit}`}
      />
      <div className="flex justify-between text-xs text-slate-600">
        <span>{prefix}{decimals > 0 ? min.toFixed(decimals) : min}{unit}</span>
        <span>{prefix}{decimals > 0 ? max.toFixed(decimals) : max}{unit}</span>
      </div>
    </div>
  )
}

// SEER formula:
// kW drawn = (capacity BTU/hr) / (SEER * 1000)
// Annual cost = kW * hours * rate
function computeACSavings(inputs: ACInputs) {
  const btuh = inputs.capacityTons * 12000
  const oldKW = btuh / (inputs.currentSEER * 1000)
  const newKW = btuh / (inputs.newSEER * 1000)
  const oldAnnualCost = oldKW * inputs.annualRunHours * inputs.electricityRate
  const newAnnualCost = newKW * inputs.annualRunHours * inputs.electricityRate
  const annualSavings = oldAnnualCost - newAnnualCost
  const tenYearSavings = annualSavings * 10
  const annualKWhSaved = (oldKW - newKW) * inputs.annualRunHours
  const co2Saved = annualKWhSaved * 1.37 // EPA avg: 1.37 lbs CO2 / kWh
  const paybackYears =
    inputs.installationCost > 0 && annualSavings > 0
      ? inputs.installationCost / annualSavings
      : null

  return {
    oldAnnualCost,
    newAnnualCost,
    annualSavings,
    tenYearSavings,
    co2Saved,
    paybackYears,
    annualKWhSaved,
  }
}

export default function ACCalculator() {
  const [inputs, setInputs] = useState<ACInputs>(DEFAULT)
  const set = (key: keyof ACInputs) => (v: number) =>
    setInputs((prev) => ({ ...prev, [key]: v }))

  const results = useMemo(() => computeACSavings(inputs), [inputs])

  return (
    <div className="p-6 lg:p-8">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Inputs Column */}
        <div>
          <h3 className="text-base font-semibold text-slate-200 mb-6 flex items-center gap-2">
            <span
              aria-hidden="true"
              className="w-6 h-6 rounded bg-blue-600/20 flex items-center justify-center text-blue-400 text-xs"
            >
              ❄️
            </span>
            AC SEER Calculator — System Parameters
          </h3>

          <div className="space-y-6" aria-label="AC SEER calculator inputs">
            <SliderField
              id="ac-capacity"
              label="Capacity (Tons)"
              value={inputs.capacityTons}
              min={1.5}
              max={5}
              step={0.5}
              unit=" tons"
              onChange={set('capacityTons')}
              decimals={1}
            />
            <SliderField
              id="ac-current-seer"
              label="Current SEER Rating"
              value={inputs.currentSEER}
              min={8}
              max={16}
              step={1}
              unit=""
              onChange={set('currentSEER')}
              decimals={0}
            />
            <SliderField
              id="ac-new-seer"
              label="New SEER Rating"
              value={inputs.newSEER}
              min={13}
              max={26}
              step={1}
              unit=""
              onChange={set('newSEER')}
              decimals={0}
            />
            <SliderField
              id="ac-run-hours"
              label="Annual Run Hours"
              value={inputs.annualRunHours}
              min={500}
              max={3000}
              step={50}
              unit=" hrs"
              onChange={set('annualRunHours')}
              decimals={0}
            />
            <SliderField
              id="ac-electricity-rate"
              label="Electricity Rate"
              value={inputs.electricityRate}
              min={0.08}
              max={0.45}
              step={0.01}
              unit="/kWh"
              prefix="$"
              decimals={2}
              onChange={set('electricityRate')}
            />

            {/* Installation Cost */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="ac-install-cost" className="text-sm font-medium text-slate-300">
                  Upfront Installation Cost{' '}
                  <span className="text-slate-600 font-normal">(optional)</span>
                </label>
                <span className="text-sm font-bold text-blue-300 tabular-nums bg-blue-500/15 px-2.5 py-0.5 rounded-md">
                  ${inputs.installationCost.toLocaleString()}
                </span>
              </div>
              <input
                id="ac-install-cost"
                type="range"
                min={0}
                max={20000}
                step={100}
                value={inputs.installationCost}
                onChange={(e) => set('installationCost')(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-blue-600"
                style={{
                  backgroundImage: `linear-gradient(to right, #2563eb ${(inputs.installationCost / 20000) * 100}%, transparent ${(inputs.installationCost / 20000) * 100}%)`,
                }}
                aria-valuemin={0}
                aria-valuemax={20000}
                aria-valuenow={inputs.installationCost}
                aria-valuetext={`$${inputs.installationCost.toLocaleString()}`}
              />
              <div className="flex justify-between text-xs text-slate-600">
                <span>$0</span>
                <span>$20,000</span>
              </div>
            </div>
          </div>
        </div>

        {/* Results Column */}
        <ResultsPanel
          oldAnnualCost={results.oldAnnualCost}
          newAnnualCost={results.newAnnualCost}
          annualSavings={results.annualSavings}
          tenYearSavings={results.tenYearSavings}
          co2Saved={results.co2Saved}
          paybackYears={results.paybackYears}
          annualKWhSaved={results.annualKWhSaved}
          accentColor="blue"
          oldLabel={`Current SEER ${inputs.currentSEER}`}
          newLabel={`New SEER ${inputs.newSEER}`}
        />
      </div>
    </div>
  )
}
